import React, { useEffect, useRef } from 'react';
import { Col, Row, Separator } from '@cuemath/comps';
import {
  Anchored,
  Bar,
  ApplyButton,
  DateTimeInput,
  Dot,
  FieldLabel,
  PollChip,
  Popover,
  Segment,
  SegmentButton,
  WindowButton,
} from '../styled-comps';
import { QM, REGIONS } from '../constants';
import InfoTip from './info';
import { fmtElapsed } from '../utils';

const PRESETS = [
  { label: 'Today', hours: null },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
];

const FilterBar = ({
  region,
  onRegionChange,
  fromRaw,
  toRaw,
  nowRaw,
  draftFrom,
  draftTo,
  onFromChange,
  onToChange,
  onApply,
  canApply,
  onPreset,
  windowOpen,
  onToggleWindow,
  secondsSinceRefresh,
  stopped,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!windowOpen) return undefined;
    const close = e => {
      if (ref.current && !ref.current.contains(e.target)) onToggleWindow(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [windowOpen, onToggleWindow]);

  return (
    <Bar>
      <Col gutter={0}>
        <FieldLabel>Region</FieldLabel>
        <Separator height={4} transparent />
        <Segment>
          {REGIONS.map(opt => (
            <SegmentButton
              key={opt.label}
              type="button"
              $on={region === opt.value}
              onClick={() => onRegionChange(opt.value)}
            >
              {opt.label}
            </SegmentButton>
          ))}
        </Segment>
      </Col>

      <Anchored ref={ref}>
        <FieldLabel>Window · IST</FieldLabel>
        <Separator height={4} transparent />
        {/* The applied window, never the draft: an edit abandoned by closing
            the popover must not leave the chip claiming a window the board
            below it is not showing. */}
        <WindowButton type="button" onClick={() => onToggleWindow(!windowOpen)}>
          <span>{fromRaw.replace('T', ' ')}</span>
          <span>→</span>
          <span>{(toRaw || nowRaw).replace('T', ' ')}</span>
          <span>▾</span>
        </WindowButton>
        {windowOpen && (
          <Popover>
            <Row gutter={0} gap={6}>
              {PRESETS.map(p => (
                <SegmentButton
                  key={p.label}
                  type="button"
                  onClick={() => onPreset(p.hours)}
                >
                  {p.label}
                </SegmentButton>
              ))}
            </Row>
            <Row gutter={0} gap={10}>
              <Col gutter={0}>
                <FieldLabel>From</FieldLabel>
                <Separator height={4} transparent />
                <DateTimeInput
                  type="datetime-local"
                  value={draftFrom}
                  onChange={e => onFromChange(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onApply()}
                />
              </Col>
              <Col gutter={0}>
                <FieldLabel>To</FieldLabel>
                <Separator height={4} transparent />
                {/* Empty, not the live "now": a value derived from the
                    1 Hz clock rewrote this controlled input at every minute
                    boundary, resetting the caret to the first segment while
                    the user was still typing the date. Chrome does not fire
                    `change` until every segment is filled, so the partial
                    entry was simply lost. The placeholder lives on the
                    button above instead. */}
                <DateTimeInput
                  type="datetime-local"
                  value={draftTo || ''}
                  placeholder={nowRaw}
                  onChange={e => onToChange(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onApply()}
                />
              </Col>
            </Row>
            <Row gutter={0}>
              <ApplyButton type="button" onClick={onApply} disabled={!canApply}>
                Apply
              </ApplyButton>
            </Row>
          </Popover>
        )}
      </Anchored>

      <PollChip $stopped={stopped}>
        <Dot $tone={stopped ? QM.TEXT_ERROR : QM.TEXT_SUCCESS} />
        <span>
          {stopped ? 'refresh stopped' : `updated ${fmtElapsed(secondsSinceRefresh)} ago`}
        </span>
        <InfoTip topic="refresh" />
      </PollChip>
    </Bar>
  );
};

export default FilterBar;
